import type { BaseQueryApi, BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import type { RootState } from '@web/app/store'
import { selectActiveServerUrl } from '@web/entities/server/model/server.slice'
import { setLogout, setTokens } from '@web/entities/user/model/auth.slice'
import type { ITokens } from '@web/entities/user/model/tokens.type'
import { ApiResource } from '@web/shared/constants/api.constants'
import { API_TAG } from '@web/shared/constants/config.constant'

type TStoreLike = Pick<BaseQueryApi, 'dispatch' | 'getState'>

export const rawBaseQuery = fetchBaseQuery({
  baseUrl: '',
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState
    const token = state.auth.accessToken

    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    return headers
  }
})

let refreshPromise: Promise<ITokens | null> | null = null

const doRefresh = async (
  store: TStoreLike,
  extraOptions?: Parameters<BaseQueryFn>[2]
): Promise<ITokens | null> => {
  const state = store.getState() as RootState
  const host = selectActiveServerUrl(state)
  const baseUrl = `${host}/${API_TAG}`
  const refreshToken = state.auth.refreshToken

  if (!refreshToken) {
    return null
  }

  const refreshResult = await rawBaseQuery(
    {
      url: `${baseUrl}${ApiResource.Auth}/refresh-token`,
      method: 'POST',
      body: { refreshToken }
    },
    store as BaseQueryApi,
    extraOptions ?? {}
  )

  if (!refreshResult.data) {
    return null
  }

  return refreshResult.data as ITokens
}

export const refreshAuthSession = async (
  store: TStoreLike,
  extraOptions?: Parameters<BaseQueryFn>[2]
): Promise<ITokens | null> => {
  const state = store.getState() as RootState

  if (!state.auth.refreshToken) {
    store.dispatch(setLogout())
    return null
  }

  if (!refreshPromise) {
    refreshPromise = doRefresh(store, extraOptions).finally(() => {
      refreshPromise = null
    })
  }

  const tokens = await refreshPromise

  if (!tokens) {
    store.dispatch(setLogout())
    return null
  }

  store.dispatch(
    setTokens({
      accessToken: tokens.token,
      refreshToken: tokens.refreshToken
    })
  )

  return tokens
}
